import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
// Icônes pour l'administration et animations
import { UserCog, Mail, Loader2, ArrowRight, Shield, Lock, Server, Code, Wrench, Star, Award, Zap, Cloud } from 'lucide-react'; 
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/use-toast';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  UserCog,
  Shield,
  Lock,
  Server,
  Code,
  Wrench,
  Star,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Récupérer l'URL de redirection depuis les paramètres
  const redirectUrl = searchParams.get('redirect');

  // Thème Login (preview > tenant)
  const loginTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'Shield',  color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Lock',    color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Server',  color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'UserCog', color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      
      // Rediriger vers l'URL d'origine ou le dashboard par défaut
      const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/admin/dashboard';
      
      toast({
        title: "Connexion réussie",
        description: "Redirection vers le tableau de bord...",
      });
      navigate(targetUrl);

    } catch (error) {
      console.error('Erreur de connexion:', error);
      toast({
        title: "Erreur de connexion",
        description: "L'adresse e-mail ou le mot de passe est incorrect.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title={`Administration - ${tenant?.name || 'Notre équipe'}`}
        description={`Accès à l'espace de gestion du site ${tenant?.name || 'notre site'}.`}
        url={`${window.location.origin}/login`}
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs tenant */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {loginTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: loginTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[loginTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(loginTheme.overlay.color1, loginTheme.overlay.opacity1)},
                    ${hexToRgba(loginTheme.overlay.color2, loginTheme.overlay.opacity2)},
                    ${hexToRgba(loginTheme.overlay.color3, loginTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !loginTheme.overlay.useGradient
                ? hexToRgba(loginTheme.overlay.color1, loginTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {loginTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(loginTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[loginTheme.icons[0].icon] || Shield, { size: loginTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {loginTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(loginTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[loginTheme.icons[1].icon] || Lock, { size: loginTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {loginTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(loginTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[loginTheme.icons[2].icon] || Server, { size: loginTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {loginTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(loginTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[loginTheme.icons[3].icon] || UserCog, { size: loginTheme.icons[3].size })}
            </motion.div>
          )}
        </div>

        {/* Contenu de la page */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4 md:pt-16 md:pb-6">
            {/* Contenu centré */}
            <div className="flex items-center justify-center py-12">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
                className="w-full max-w-md"
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-center mb-8"
                >
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }} 
                    animate={{ opacity: 1, scale: 1 }} 
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex justify-center mb-6"
                  >
                    <div className="relative">
                      <div 
                        className="absolute -inset-4 rounded-full blur-lg opacity-50"
                        style={{ 
                          background: `linear-gradient(45deg, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})` 
                        }}
                      ></div>
                      <UserCog 
                        className="w-16 h-16 relative" 
                        style={{ color: tenant?.config?.color1 || '#fe8d17' }}
                      />
                    </div>
                  </motion.div>
                  <motion.h1 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-3xl font-bold mb-2"
                  >
                    <span 
                      className="bg-gradient-to-r text-transparent bg-clip-text"
                      style={{ 
                        backgroundImage: `linear-gradient(45deg, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})` 
                      }}
                    >
                      Administration
                    </span>
                  </motion.h1>
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-white/70"
                  >
                    Gestion du contenu de {tenant?.name || 'votre site'}.
                  </motion.p>
                </motion.div>

                {/* Formulaire de connexion */}
                <motion.div 
                  initial={{ opacity: 0, y: 30 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-xl"
                >
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-white/50" />
                        </div>
                        <input
                          type="email"
                          required
                          className="block w-full pl-10 pr-3 py-2.5 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300"
                          style={{ 
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="votre-email@admin.com"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Mot de passe
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-white/50" />
                        </div>
                        <input
                          type="password"
                          required
                          className="block w-full pl-10 pr-3 py-2.5 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300"
                          style={{ 
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-xl font-semibold text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                      style={{ 
                        backgroundColor: tenant?.config?.color1 || '#fe8d17',
                        color: tenant?.config?.color3 || '#ffffff'
                      }}
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}dd`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                        }
                      }}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <span>Se connecter</span>
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};